import { samplesConfigObj } from '@/components/Glossary/Sections/SampleTypes/SamplesConfig';
import { DateTimeEditor } from '@/components/UI/SpreadSheet/editors/DateTimeEditor';
import type { CustomColumnSettings } from '@/components/UI/SpreadSheet/SpreadSheet.model';
import type { SpreadsheetSettings } from '@/components/UI/SpreadSheet/SpreadSheet.model';
import { _isEmpty, _isEmptyString, _isNil, _isNotDeepEqual, _isNumber, _notNil } from '@/littledash';
import type { MetadataField, MetadataFieldValueTypes, MetadataFieldWithValue } from '@/model/Metadata.model';
import type { SampleDetailV1, SampleEdit, SampleMeasurementKey } from '@/model/Sample.model';
import type { TeamGlossaryV1 } from '@/model/Glosary.model';
import { ApiService } from '@/support/ApiService';
import { MultiSelectEditor } from '@/components/UI/SpreadSheet/editors/MultiSelectEditor';
import { defaultMetadataToColumnSettingMapper, formatColumnSettingsDataValue } from '@/support/hot';
import { formatNumberComingIn } from '@/helpers';
import { DateUtils } from '@/utils/Date.utils';
import type { CellValue, RowObject } from '@/support/hot.types';
import type { CustomSample } from './BulkSampleEdit';
import type { CustomColumnDataGetterSetterFunction } from '@/components/UI/SpreadSheet/SpreadSheet.model';

const isSampleMeasurementKey = (key: string): key is SampleMeasurementKey => {
  return ['weight', 'width', 'length', 'depth', 'volume'].includes(key);
};

export type ColumnWrapper = Pick<SpreadsheetSettings, 'columns' | 'colHeaders'>;

export const LINKED_SAMPLE_UNIT_TOOLTIP = 'Units cannot be changed for samples linked to a sample plan';

/**
 * Builds a Handsontable `cells` function that locks the unit columns of any sample linked to a
 * sample definition (i.e. created from a sampling plan). Linked samples must keep their
 * plan-defined units, so their unit dropdown is made read-only while the numeric value column
 * stays editable.
 *
 * Applying the lock declaratively via `cells` (rather than imperatively via `setCellMeta`) keeps
 * it in place across Handsontable re-renders and `updateSettings({ columns })` calls, which
 * otherwise reset per-cell meta and let the unit become editable again — the source of the
 * bulk-edit 400 (BNCH-154557).
 */
export const createUnitLockCells = (
  columns: Array<{ isUnitColumn?: boolean }>,
  getSample: (row: number) => Pick<CustomSample, 'sample_definition_api_id'> | undefined
): ((row: number, col: number) => object) | undefined => {
  const unitColumnIndices = columns.reduce<Set<number>>((acc, column, col) => {
    if (column?.isUnitColumn === true) {
      acc.add(col);
    }
    return acc;
  }, new Set<number>());

  if (unitColumnIndices.size === 0) {
    return undefined;
  }

  return (row: number, col: number): object => {
    if (unitColumnIndices.has(col) && _notNil(getSample(row)?.sample_definition_api_id)) {
      return { readOnly: true, className: 'htDimmed', linkedTooltipMessage: LINKED_SAMPLE_UNIT_TOOLTIP };
    }
    return {};
  };
};

const isSampleIdUniqueInTeam = async (
  hotInstance: { getData: () => Array<Array<unknown>> },
  row: number,
  sampleId: string,
  callback: (valid: boolean) => void,
  originalSampleId?: string
): Promise<void> => {
  if (_isNil(sampleId)) {
    callback(false);
    return;
  }

  if (_notNil(originalSampleId) && sampleId === originalSampleId) {
    callback(true);
    return;
  }

  const sampleApiIdColumn = 0;
  const sampleIdColumn = 1;

  const cellData = hotInstance.getData();
  const otherRows = [...cellData.slice(0, row), ...cellData.slice(row + 1)];

  if (otherRows.some((sample) => sample[sampleIdColumn] === sampleId)) {
    callback(false);
  } else {
    const currentRow = cellData[row];
    const result = await ApiService.call({
      endpoint: 'GET /api/v1/samples/{sampleApiId}/unique/{sampleId}',
      path: { sampleApiId: String(currentRow[sampleApiIdColumn] ?? ''), sampleId },
    });
    callback(result.type === 'success' ? (result.body.success ?? false) : false);
  }
};

const detailValueValidator = (value: unknown): boolean => {
  const strValue = typeof value === 'string' ? value : String(value ?? '');
  return _isNil(value) || _isEmptyString(strValue) || _isNumber(Number(strValue));
};

const detailUnitValidator = (
  type: SampleMeasurementKey,
  units: Array<string>,
  rowData: CustomSample,
  unit: unknown
): boolean => {
  const strUnit = typeof unit === 'string' ? unit : '';
  const valueData = rowData?.details?.[type]?.value;
  const isEmpty = _isNil(unit) || _isEmptyString(strUnit);

  return isEmpty ? _isNil(valueData) || _isEmptyString(valueData) : units.includes(strUnit);
};

const detailValueSetter =
  (type: SampleMeasurementKey) =>
  (row: RowObject, value: CellValue): CellValue => {
    const currentDetails = (row as CustomSample)?.details?.[type];
    if (_notNil(currentDetails) && typeof value !== 'undefined') {
      row.details[type].value = value;
    } else if (_isNil(currentDetails) && typeof value !== 'undefined') {
      row.details[type] = { value, key: type };
    }
    return currentDetails?.value;
  };

const detailUnitSetter =
  (type: SampleMeasurementKey) =>
  (row: RowObject, value: CellValue): CellValue => {
    const currentDetails = (row as CustomSample)?.details?.[type];
    if (_notNil(currentDetails) && typeof value !== 'undefined') {
      row.details[type].unit = value;
    } else if (_isNil(currentDetails) && typeof value !== 'undefined') {
      row.details[type] = { unit: value, key: type };
    }
    return currentDetails?.unit;
  };

export const timepointAccessor: CustomColumnDataGetterSetterFunction = function (
  row: RowObject | Array<CellValue>,
  value?: CellValue
): CellValue {
  const sample = row as CustomSample;

  if (arguments.length > 1) {
    if (Array.isArray(value)) {
      sample.timepoint = value.length > 0 ? value[0] : '';
    } else if (typeof value === 'string') {
      sample.timepoint = value;
    } else if (_isNil(value)) {
      sample.timepoint = '';
    }
  }

  return sample.timepoint ? [sample.timepoint] : [];
};

export const returnInitialMetaValues = (
  metadata: Array<MetadataFieldWithValue>
): Record<string, MetadataFieldValueTypes> =>
  metadata.reduce<Record<string, MetadataFieldValueTypes>>((acc, meta) => {
    const title = formatColumnSettingsDataValue(meta.title);
    if (Array.isArray(meta.value)) {
      acc[title] = meta.value.join(',');
    } else {
      acc[title] = meta.value;
    }
    return acc;
  }, {});

export const baseColumnData = (
  sampleType?: TeamGlossaryV1,
  plannedSamplesEnabled = false,
  getOriginalSampleId?: (sampleApiId: string) => string | undefined
): ColumnWrapper => {
  const sampleApiIdColumn = 0;
  const columns: Array<CustomColumnSettings> = [
    {
      data: 'api_id',
      type: 'text',
      readOnly: true,
      width: 150,
    },
    {
      data: 'sample_id',
      type: 'text',
      allowInvalid: true,
      validator: function (
        this: { instance: { getData: () => Array<Array<unknown>> }; row: number },
        value: string,
        callback: (valid: boolean) => void
      ) {
        const currentRow = this.instance.getData()[this.row];
        const sampleApiId = String(currentRow?.[sampleApiIdColumn] ?? '');
        const originalSampleId = getOriginalSampleId?.(sampleApiId);
        return isSampleIdUniqueInTeam(this.instance, this.row, value, callback, originalSampleId);
      },
      validationErrorMessage: () => 'Sample ID is required and must be unique in team',
      width: 150,
    },
    {
      data: 'subject.name',
      readOnly: true,
      type: 'text',
      width: 150,
    },
    {
      data: 'type',
      readOnly: true,
      type: 'text',
      width: 150,
    },
    {
      data: 'date',
      validator: function (
        this: { instance: { getSourceDataAtRow: (row: number) => unknown }; row: number },
        value: string,
        callback: (valid: boolean) => void
      ) {
        const sample = this.instance.getSourceDataAtRow(this.row) as CustomSample;
        const isCollected = sample?.status === 'collected';
        if (isCollected) {
          callback(DateUtils.isValidDateTime(value));
        } else {
          callback(_isNil(value) || _isEmptyString(value) || DateUtils.isValidDateTime(value));
        }
      },
      type: DateTimeEditor.type,
      width: 200,
      validationErrorMessage: () => 'Collected samples require a valid collected date',
    },
    ...(plannedSamplesEnabled
      ? [
          {
            data: timepointAccessor as unknown as string,
            type: 'timepoint',
            singleValue: true,
            allow_zero: true,
            allow_negative: true,
            width: 150,
          } as CustomColumnSettings,
        ]
      : []),
    {
      data: 'comments',
      type: 'text',
      width: 200,
      validator: (value: unknown, callback: (valid: boolean) => void) => {
        callback(String(value ?? '').length <= 255);
      },
      validationErrorMessage: () => 'Comments have a max length of 255 characters',
    },
    ...(sampleType?.options?.details?.filter(isSampleMeasurementKey).flatMap<CustomColumnSettings>((type) => [
      {
        data: detailValueSetter(type) as unknown as string,
        type: 'numeric',
        validator: (value: unknown, callback: (valid: boolean) => void) => {
          callback(detailValueValidator(value));
        },
        width: 150,
        validationErrorMessage: () => 'Value must be numeric',
      },
      {
        data: detailUnitSetter(type) as unknown as string,
        type: 'dropdown',
        source: samplesConfigObj[type].units,
        width: 150,
        isUnitColumn: true,
        validator: function (
          this: { instance: { getSourceDataAtRow: (row: number) => unknown }; row: number },
          value: unknown,
          callback: (valid: boolean) => void
        ) {
          const rowData = this.instance.getSourceDataAtRow(this.row) as CustomSample;
          callback(detailUnitValidator(type, samplesConfigObj[type].units, rowData, value));
        },
        validationErrorMessage: () => 'Unit is required when a value is present and must be a valid option',
      },
    ]) ?? []),
  ];

  const colHeaders = [
    'Sample API ID',
    'Sample ID',
    'Animal name',
    'Sample type',
    'Date taken',
    ...(plannedSamplesEnabled ? ['Timepoint'] : []),
    'Comments',
    ...(sampleType?.options?.details?.filter(isSampleMeasurementKey).flatMap((type) => {
      const capitalized = `${type.slice(0, 1).toUpperCase()}${type.slice(1)}`;
      return [`${capitalized} Value`, `${capitalized} Unit`];
    }) ?? []),
  ];

  return { columns, colHeaders };
};

export const getBaseSettings = (
  plannedSamplesEnabled = false,
  getOriginalSampleId?: (sampleApiId: string) => string | undefined
): SpreadsheetSettings => ({
  ...baseColumnData(undefined, plannedSamplesEnabled, getOriginalSampleId),
  hiddenColumns: { columns: [0] },
  columnSorting: false,
  className: 'htMiddle',
  manualColumnResize: true,
  contextMenu: false,
  rowHeights: 50,
  height: 500,
  rowHeaders: true,
});

export const generateColumnData = (
  displayedSampleMetadata: Array<MetadataField>,
  sampleType?: TeamGlossaryV1,
  plannedSamplesEnabled = false,
  getOriginalSampleId?: (sampleApiId: string) => string | undefined
): ColumnWrapper =>
  displayedSampleMetadata.reduce<ColumnWrapper>(
    ({ columns, colHeaders }, metadata) => {
      if (metadata.field_type) {
        const { columnHeader, columnSettings } = defaultMetadataToColumnSettingMapper(metadata);
        return {
          colHeaders: [...((colHeaders as Array<string>) ?? []), columnHeader],
          columns: [...((columns as Array<CustomColumnSettings>) ?? []), columnSettings as CustomColumnSettings],
        };
      }
      return { colHeaders, columns };
    },
    baseColumnData(sampleType, plannedSamplesEnabled, getOriginalSampleId)
  );

const createMetaMap = (
  meta: Record<string, MetadataFieldValueTypes>,
  columns: Array<CustomColumnSettings>
): Record<string, MetadataFieldValueTypes> =>
  Object.entries(meta).reduce<Record<string, MetadataFieldValueTypes>>((acc, [title, value]) => {
    if (_notNil(title)) {
      let newValue: MetadataFieldValueTypes;
      const cellData = columns.find((c) => c.data === `meta.${title}`);
      if (_notNil(cellData)) {
        const { type, metaID, isMultiSelect } = cellData;
        // Never emit `undefined`: the JSON wire format silently drops it and the API rejects
        // the payload ("value … is not compatible with the field type"). An untouched cell
        // (or one seeded as `undefined` from a metadata field the API returned without a value)
        // must be omitted; explicit clears still arrive here as '' / null and are kept.
        if (value === undefined) {
          return acc;
        }
        if (_notNil(value) && type === MultiSelectEditor.type && typeof value === 'string' && isMultiSelect !== false) {
          newValue = _isEmpty(value) ? null : value.split(',');
        } else if (type === 'numeric-expo' && _notNil(value)) {
          newValue = formatNumberComingIn(value);
        } else {
          newValue = value;
        }
        if (_notNil(metaID)) {
          acc[metaID] = newValue;
        }
      }
    }
    return acc;
  }, {});

export const generateSampleBulkUpdateList = (
  initialSamples: Array<CustomSample> = [],
  editedSamples: Array<CustomSample> = [],
  columns: Array<CustomColumnSettings>
): SampleEdit => {
  const initialSamplesMap = new Map(initialSamples.map((group) => [group.id, group]));

  return editedSamples.reduce<SampleEdit>((acc, sample) => {
    const { id, api_id, details, date, sample_id, comments, meta, timepoint } = sample;

    if (_isNotDeepEqual(initialSamplesMap.get(id), sample)) {
      acc.push({
        api_id,
        details: Object.values(details) as Array<SampleDetailV1>,
        date,
        sample_id,
        comments,
        meta: createMetaMap(meta, columns),
        timepoint: timepoint || null,
      });
    }
    return acc;
  }, []);
};
